import { FC } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import { useAuth } from '../../../services/useAuth'
import { getProductCurrency } from '../../../lib/product'
import { getSupplierDeliveryDetails, isFreeDelivery } from '../../../lib/service'
import { formatPriceNumber } from '../../../utils/math'
import { Product, Supplier } from '../../../utils/types/Product'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import useSingleProductSupplierContent from '../../../services/strapi/useSingleProductSupplierContent'

interface DeliveryTermsProps {
  product: Product
  supplier?: Supplier
}

const DeliveryTerms: FC<DeliveryTermsProps> = ({
  product,
  supplier,
}) => {
  const { locale: countryAndLocale } = useRouter()
  const { t } = useTranslation('products')
  const { isVatIncluded } = useAuth()

  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const currency = getProductCurrency(product, country)
  const supplierName = supplier?.['supplier.label']
  const {
    minimumOrderValue: mov,
    freeShippingLimit: freeShippingStartsfrom,
    deliveryCost: supplierShippingFee,
  } = getSupplierDeliveryDetails(supplier, country, isVatIncluded)

  const isFreeDeliveryAvailable = isFreeDelivery(product, country, isVatIncluded)

  const supplierContent = useSingleProductSupplierContent(supplier?.['supplier.code'] || '')

  return (
    <div>
      {isFreeDeliveryAvailable ? (
        <>
          <p className="margin-bottom-spacer-half">
            {t('Free delivery for all {{supplier}} products', { supplier: supplierName })}
          </p>
          <div>
            {supplierContent}
          </div>
        </>
      )
        : (
          <>
            <p className="margin-bottom-spacer-half">
              {t('Free delivery when you order more than {{ amount }} {{ currency }} from {{ supplier }}',
                {
                  amount: formatPriceNumber(freeShippingStartsfrom),
                  currency,
                  supplier: supplier && supplier['supplier.label'],
                })}
            </p>
            <p className="margin-top-spacer-half">
              {`${t('Supplier shipping fee')} ${formatPriceNumber(supplierShippingFee)} ${currency}`}
            </p>
            <p className="margin-top-spacer-half">
              {t('Brand minimum {{price}} {{currency}}', { price: formatPriceNumber(mov), currency })}
            </p>
            <div className="margin-top-spacer-double">
              {supplierContent}
            </div>
          </>
        )}
    </div>
  )
}

export default DeliveryTerms
